import React, { useState, useEffect } from 'react';
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { useAuth } from './AuthContext';
import './css/KpiWidget.css';
import './css/BaobabDesignSystem.css';

const KpiWidget = ({ type, refreshInterval = 30000 }) => {
  const [kpiData, setKpiData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  const { user } = useAuth();
  const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

  useEffect(() => {
    fetchKpiData();
    
    // Auto-refresh selon l'intervalle spécifié
    const interval = setInterval(fetchKpiData, refreshInterval);
    
    return () => clearInterval(interval);
  }, [type, refreshInterval]);

  const fetchKpiData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/api/kpis/pipeline`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setKpiData(data);
        setLastUpdated(new Date());
        setError(null);
      } else {
        setError('Erreur lors du chargement des KPIs');
      }
    } catch (error) {
      console.error('Error fetching KPI data:', error);
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatPercentage = (value) => {
    return `${value.toFixed(1)}%`;
  };

  const getColorByValue = (value, thresholds) => {
    if (value >= thresholds.good) return '#22C55E';
    if (value >= thresholds.warning) return '#F59E0B';
    return '#EF4444';
  };

  if (loading) {
    return (
      <div className="kpi-widget loading">
        <div className="kpi-spinner"></div>
        <p>Chargement des KPIs...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="kpi-widget error">
        <div className="error-icon"></div>
        <p>{error}</p>
        <button onClick={fetchKpiData} className="retry-btn">
          Réessayer
        </button>
      </div>
    );
  }

  if (!kpiData) {
    return (
      <div className="kpi-widget error">
        <div className="error-icon">📊</div>
        <p>Aucune donnée KPI disponible</p>
        <button onClick={fetchKpiData} className="retry-btn">
          Charger les données
        </button>
      </div>
    );
  }

  // Configuration des seuils pour les couleurs
  const thresholds = {
    win_rate: { good: 70, warning: 50 },
    conversion: { good: 60, warning: 40 },
    cycle_time: { good: 30, warning: 45 } // inversé (plus bas = mieux)
  };

  switch (type) {
    case 'pipeline_total':
      return (
        <div className="kpi-widget pipeline-total">
          <div className="kpi-header">
            <h3 className="kpi-title">📊 Valeur Pipeline Total</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="kpi-value-large">
            {formatCurrency(kpiData.pipeline_total)}
          </div>
          <div className="kpi-subtitle">
            Opportunités en cours ({Object.values(kpiData.stage_distribution).reduce((a, b) => a + b, 0) - kpiData.stage_distribution.gagnée - kpiData.stage_distribution.perdue} actives)
          </div>
        </div>
      );

    case 'win_rate':
      const winRateColor = getColorByValue(kpiData.win_rate, thresholds.win_rate);
      return (
        <div className="kpi-widget win-rate">
          <div className="kpi-header">
            <h3 className="kpi-title">🎯 Taux de Conversion</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="kpi-value-large" style={{ color: winRateColor }}>
            {formatPercentage(kpiData.win_rate)}
          </div>
          <div className="kpi-metrics">
            <div className="kpi-metric">
              <span className="metric-label">Gagnées:</span>
              <span className="metric-value">{kpiData.stage_distribution.gagnée}</span>
            </div>
            <div className="kpi-metric">
              <span className="metric-label">Perdues:</span>
              <span className="metric-value">{kpiData.stage_distribution.perdue}</span>
            </div>
          </div>
        </div>
      );

    case 'conversion_stages':
      const conversionData = Object.entries(kpiData.conversion_rates).map(([key, value]) => ({
        name: key.replace(/_to_/g, ' → ').replace(/_/g, ' '),
        value: value,
        color: getColorByValue(value, thresholds.conversion)
      }));

      return (
        <div className="kpi-widget conversion-stages">
          <div className="kpi-header">
            <h3 className="kpi-title">🔄 Conversion par Étape</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={conversionData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis 
                  dataKey="name" 
                  angle={-45}
                  textAnchor="end"
                  height={80}
                  fontSize={10}
                />
                <YAxis />
                <Tooltip formatter={(value) => [formatPercentage(value), 'Conversion']} />
                <Bar dataKey="value" fill="#22C55E" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      );

    case 'cycle_time':
      const cycleTimeColor = kpiData.average_cycle_time_days <= thresholds.cycle_time.good ? '#22C55E' : 
                             kpiData.average_cycle_time_days <= thresholds.cycle_time.warning ? '#F59E0B' : '#EF4444';
      return (
        <div className="kpi-widget cycle-time">
          <div className="kpi-header">
            <h3 className="kpi-title">⏱️ Cycle de Vente Moyen</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="kpi-value-large" style={{ color: cycleTimeColor }}>
            {kpiData.average_cycle_time_days} jours
          </div>
          <div className="kpi-subtitle">
            Temps moyen de conversion
          </div>
        </div>
      );

    case 'forecast':
      const forecastData = [
        { name: '30 jours', value: kpiData.forecast_30_days, color: '#22C55E' },
        { name: '60 jours', value: kpiData.forecast_60_days, color: '#3B82F6' },
        { name: '90 jours', value: kpiData.forecast_90_days, color: '#F59E0B' }
      ];

      return (
        <div className="kpi-widget forecast">
          <div className="kpi-header">
            <h3 className="kpi-title">📈 Prévisions de Ventes</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="forecast-metrics">
            {forecastData.map((item, index) => (
              <div key={index} className="forecast-item">
                <div className="forecast-period" style={{ color: item.color }}>
                  {item.name}
                </div>
                <div className="forecast-value">
                  {formatCurrency(item.value)}
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case 'stage_distribution':
      const stageData = Object.entries(kpiData.stage_distribution)
        .filter(([stage, count]) => count > 0)
        .map(([stage, count]) => ({
          name: stage.charAt(0).toUpperCase() + stage.slice(1),
          value: count,
          percentage: ((count / Object.values(kpiData.stage_distribution).reduce((a, b) => a + b, 0)) * 100).toFixed(1)
        }));

      const COLORS = ['#6B7280', '#3B82F6', '#F59E0B', '#EF4444', '#22C55E', '#9CA3AF'];

      return (
        <div className="kpi-widget stage-distribution">
          <div className="kpi-header">
            <h3 className="kpi-title">📊 Répartition par Étape</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={stageData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percentage }) => `${name}: ${percentage}%`}
                  outerRadius={70}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {stageData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      );

    case 'monthly_performance':
      const currentMonth = new Date().toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
      const currentMonthOpportunities = kpiData.stage_distribution ? 
        Object.values(kpiData.stage_distribution).reduce((sum, count) => sum + count, 0) : 0;
      
      // Estimation basée sur les données disponibles
      const monthlyValue = kpiData.pipeline_total || 0;

      return (
        <div className="kpi-widget monthly-performance">
          <div className="kpi-header">
            <h3 className="kpi-title">📅 Performance {currentMonth}</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="kpi-value-large" style={{ color: '#3B82F6' }}>
            {currentMonthOpportunities} opportunités
          </div>
          <div className="kpi-subtitle">
            Pipeline actuel: {formatCurrency(monthlyValue)}
          </div>
          <div className="kpi-metrics">
            <div className="kpi-metric">
              <span className="metric-label">Gagnées:</span>
              <span className="metric-value">{kpiData.stage_distribution?.gagnée || 0}</span>
            </div>
            <div className="kpi-metric">
              <span className="metric-label">En cours:</span>
              <span className="metric-value">{currentMonthOpportunities - (kpiData.stage_distribution?.gagnée || 0) - (kpiData.stage_distribution?.perdue || 0)}</span>
            </div>
          </div>
        </div>
      );

    case 'commercial_ranking':
      return (
        <div className="kpi-widget commercial-ranking">
          <div className="kpi-header">
            <h3 className="kpi-title">🏆 Performance par Étape</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="commercial-ranking-list">
            {Object.entries(kpiData.stage_distribution || {})
              .sort(([,a], [,b]) => b - a)
              .slice(0, 4)
              .map(([stage, count], index) => (
                <div key={index} className="commercial-rank-item">
                  <div className="rank-position">#{index + 1}</div>
                  <div className="commercial-info">
                    <div className="commercial-name">{stage.charAt(0).toUpperCase() + stage.slice(1)}</div>
                    <div className="commercial-stats">
                      {count} opportunités
                    </div>
                  </div>
                </div>
              ))}
          </div>
        </div>
      );

    case 'pipeline_velocity':
      const velocityScore = kpiData.average_cycle_time_days > 0 ? 
        Math.max(0, 100 - kpiData.average_cycle_time_days) : 0;

      return (
        <div className="kpi-widget pipeline-velocity">
          <div className="kpi-header">
            <h3 className="kpi-title">⚡ Vélocité Pipeline</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="kpi-value-large" style={{ color: velocityScore >= 70 ? '#22C55E' : velocityScore >= 40 ? '#F59E0B' : '#EF4444' }}>
            {velocityScore.toFixed(0)}%
          </div>
          <div className="kpi-subtitle">
            Score de vélocité
          </div>
          <div className="kpi-metrics">
            <div className="kpi-metric">
              <span className="metric-label">Cycle moyen:</span>
              <span className="metric-value">{kpiData.average_cycle_time_days} jours</span>
            </div>
            <div className="kpi-metric">
              <span className="metric-label">Actives:</span>
              <span className="metric-value">{(kpiData.stage_distribution?.prospection || 0) + (kpiData.stage_distribution?.qualification || 0) + (kpiData.stage_distribution?.proposition || 0) + (kpiData.stage_distribution?.négociation || 0)}</span>
            </div>
          </div>
        </div>
      );

    case 'opportunities_trend':
      // Calcul de tendance basé sur le win rate et la distribution
      const activePipeline = (kpiData.stage_distribution?.prospection || 0) + 
                           (kpiData.stage_distribution?.qualification || 0) + 
                           (kpiData.stage_distribution?.proposition || 0) + 
                           (kpiData.stage_distribution?.négociation || 0);
      
      const closedTotal = (kpiData.stage_distribution?.gagnée || 0) + (kpiData.stage_distribution?.perdue || 0);
      const trendScore = closedTotal > 0 ? (activePipeline / closedTotal) * 100 : 0;

      return (
        <div className="kpi-widget opportunities-trend">
          <div className="kpi-header">
            <h3 className="kpi-title">📈 Tendance Pipeline</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="kpi-value-large" style={{ color: trendScore >= 100 ? '#22C55E' : trendScore >= 50 ? '#F59E0B' : '#EF4444' }}>
            {trendScore.toFixed(0)}%
          </div>
          <div className="kpi-subtitle">
            Ratio actives/clôturées
          </div>
          <div className="kpi-metrics">
            <div className="kpi-metric">
              <span className="metric-label">Actives:</span>
              <span className="metric-value">{activePipeline}</span>
            </div>
            <div className="kpi-metric">
              <span className="metric-label">Clôturées:</span>
              <span className="metric-value">{closedTotal}</span>
            </div>
          </div>
        </div>
      );

    case 'client_acquisition':
      const acquisitionRate = kpiData.stage_distribution?.gagnée || 0;
      const totalClients = Object.values(kpiData.stage_distribution || {}).reduce((sum, count) => sum + count, 0);
      const acquisitionPercentage = totalClients > 0 ? (acquisitionRate / totalClients) * 100 : 0;

      return (
        <div className="kpi-widget client-acquisition">
          <div className="kpi-header">
            <h3 className="kpi-title">🎯 Taux d'Acquisition</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="kpi-value-large" style={{ color: '#8B5CF6' }}>
            {acquisitionPercentage.toFixed(1)}%
          </div>
          <div className="kpi-subtitle">
            Clients acquis / Total
          </div>
          <div className="kpi-metrics">
            <div className="kpi-metric">
              <span className="metric-label">Gagnées:</span>
              <span className="metric-value">{acquisitionRate}</span>
            </div>
            <div className="kpi-metric">
              <span className="metric-label">Total:</span>
              <span className="metric-value">{totalClients}</span>
            </div>
          </div>
        </div>
      );

    case 'revenue_projection':
      // Projection basée sur les forecasts disponibles
      const projectedRevenue = kpiData.forecast_90_days || 0;
      const guaranteedRevenue = kpiData.forecast_30_days || 0;
      const projectionGrowth = guaranteedRevenue > 0 ? ((projectedRevenue - guaranteedRevenue) / guaranteedRevenue) * 100 : 0;

      return (
        <div className="kpi-widget revenue-projection">
          <div className="kpi-header">
            <h3 className="kpi-title">💰 Projection 90j</h3>
            <div className="kpi-refresh" title={`Dernière MAJ: ${lastUpdated?.toLocaleTimeString('fr-FR')}`}>
              🔄
            </div>
          </div>
          <div className="kpi-value-large" style={{ color: '#059669' }}>
            {formatCurrency(projectedRevenue)}
          </div>
          <div className="kpi-subtitle">
            Revenus projetés 3 mois
          </div>
          <div className="kpi-metrics">
            <div className="kpi-metric">
              <span className="metric-label">30 jours:</span>
              <span className="metric-value">{formatCurrency(guaranteedRevenue)}</span>
            </div>
            <div className="kpi-metric">
              <span className="metric-label">Croissance:</span>
              <span className="metric-value" style={{ color: projectionGrowth >= 0 ? '#22C55E' : '#EF4444' }}>
                {projectionGrowth >= 0 ? '+' : ''}{projectionGrowth.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      );

    default:
      return (
        <div className="kpi-widget error">
          <p>Type de widget non reconnu: {type}</p>
        </div>
      );
  }
};

export default KpiWidget;